import React, { useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Typography } from '@mui/material';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.scss';
import { OtherLoanCharacteristicsSectionProps } from '../types';
import { TableRow } from './tableRow';
import {
  mortgagePaymentTypes,
  mortgageSecurityPriorityRankingTypes,
  mortgageSecurityTypes,
  propertyListingTypes,
  propertyValuationMethodTypes,
  residentialStatusTypes,
  sourceOfBusinessTypes
} from './utils/statusCodes';
import CopyContentToClipBoard from './CopyContentToClipBoard';

export const OtherLoanCharacteristicsSection = ({
  isCollapsed,
  onToggle,
  otherLoanData
}: OtherLoanCharacteristicsSectionProps) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('Copied'),
      subHeader: String(' ')
    }
  });

  return (
    <>
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>

      <Box className={styles.accordionContainer} onClick={onToggle}>
        <Typography variant="h6" className={styles.accordionTitle}>
          {t('Other-Loan-Characteristics')}
        </Typography>
        <Button
          type="button"
          className={styles.toggleButton}
          id={styles.toggleButton}
          name="lender"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY}
          onClick={onToggle}
          leadingIcon={() => (
            <CmhcIcon
              className={styles.accordionIcon}
              size={CmhcIconSize.MEDIUM}
              type={isCollapsed ? 'icon_chevron_up' : 'icon_chevron_down'}
            />
          )}
          text={{
            value: ''
          }}
        />
      </Box>
      {!isCollapsed && (
        <Box className={styles.accordionBody}>
          <Box className={styles.tableContainer}>
            <TableRow
              leftTitle={t('Multi-Component-Key')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  clipboardContent={otherLoanData?.multiComponentKey || ''}
                  showClipButton={Boolean(otherLoanData?.multiComponentKey)}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.multiComponentKey)}
                    cellContent={<span>{otherLoanData?.multiComponentKey}</span>}
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle=""
              rightValue={() => <></>}
              className={styles.hideCellTopBorder}
            />
            <TableRow
              leftTitle={t('Mortgage-Security-Priority-Ranking')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(
                    mortgageSecurityPriorityRankingTypes(
                      otherLoanData?.mortgageSecurityPriorityRanking || '',
                      t
                    )
                  )}
                  clipboardContent={mortgageSecurityPriorityRankingTypes(
                    otherLoanData?.mortgageSecurityPriorityRanking || '',
                    t
                  )}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.mortgageSecurityPriorityRanking)}
                    cellContent={
                      <span>
                        {mortgageSecurityPriorityRankingTypes(
                          otherLoanData?.mortgageSecurityPriorityRanking || '',
                          t
                        )}
                      </span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('Mortgage-Security-Type')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(
                    mortgageSecurityTypes(otherLoanData?.mortgageSecurityType || '', t)
                  )}
                  clipboardContent={mortgageSecurityTypes(
                    otherLoanData?.mortgageSecurityType || '',
                    t
                  )}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.mortgageSecurityType)}
                    cellContent={
                      <span>
                        {mortgageSecurityTypes(otherLoanData?.mortgageSecurityType || '', t)}
                      </span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
            />
            <TableRow
              leftTitle={t('Property-Valuation-Method')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(
                    propertyValuationMethodTypes(otherLoanData?.propertyValuationMethod || '', t)
                  )}
                  clipboardContent={propertyValuationMethodTypes(
                    otherLoanData?.propertyValuationMethod || '',
                    t
                  )}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={
                      otherLoanData?.propertyValuationMethod
                        ? String('1')
                        : String(otherLoanData?.propertyValuationMethod)
                    }
                    cellContent={
                      <span>
                        {propertyValuationMethodTypes(
                          otherLoanData?.propertyValuationMethod || '',
                          t
                        )}
                      </span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('Property-Valuation-Method-Other')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(otherLoanData?.propertyValuationMethodOther)}
                  clipboardContent={otherLoanData?.propertyValuationMethodOther || ''}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.propertyValuationMethodOther)}
                    cellContent={<span>{otherLoanData?.propertyValuationMethodOther}</span>}
                  />
                </CopyContentToClipBoard>
              )}
            />

            <TableRow
              leftTitle={t('Mortgate-Payment-Frequency')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(
                    mortgagePaymentTypes(otherLoanData?.mortgatePaymentFrequency || '', t)
                  )}
                  clipboardContent={mortgagePaymentTypes(
                    otherLoanData?.mortgatePaymentFrequency || '',
                    t
                  )}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={
                      otherLoanData?.mortgatePaymentFrequency
                        ? String('1')
                        : String(otherLoanData?.mortgatePaymentFrequency)
                    }
                    cellContent={
                      <span>
                        {mortgagePaymentTypes(otherLoanData?.mortgatePaymentFrequency || '', t)}
                      </span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('Mortgate-Principal-and-Interest-Payment-Amount')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(otherLoanData?.mortgatePrincipalAndInterestPaymentAmount)}
                  clipboardContent={otherLoanData?.mortgatePrincipalAndInterestPaymentAmount || ''}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.mortgatePrincipalAndInterestPaymentAmount)}
                    cellContent={
                      <span>{otherLoanData?.mortgatePrincipalAndInterestPaymentAmount}</span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
            />
            <TableRow
              leftTitle={t('Residential-Status')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(
                    residentialStatusTypes(otherLoanData?.residentialStatus || '', t)
                  )}
                  clipboardContent={residentialStatusTypes(
                    otherLoanData?.residentialStatus || '',
                    t
                  )}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.residentialStatus)}
                    cellContent={
                      <span>
                        {residentialStatusTypes(otherLoanData?.residentialStatus || '', t)}
                      </span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('Property-Listing-Type')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(
                    propertyListingTypes(otherLoanData?.propertyListingType || '', t)
                  )}
                  clipboardContent={propertyListingTypes(
                    otherLoanData?.propertyListingType || '',
                    t
                  )}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.propertyListingType)}
                    cellContent={
                      <span>
                        {propertyListingTypes(otherLoanData?.propertyListingType || '', t)}
                      </span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
            />
            <TableRow
              leftTitle={t('Source-of-Business')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(
                    sourceOfBusinessTypes(otherLoanData?.sourceOfBusiness || '', t)
                  )}
                  clipboardContent={sourceOfBusinessTypes(otherLoanData?.sourceOfBusiness || '', t)}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.sourceOfBusiness)}
                    cellContent={
                      <span>{sourceOfBusinessTypes(otherLoanData?.sourceOfBusiness || '', t)}</span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('Broker-Mortgage-Specialist-Name')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(otherLoanData?.brokerMortgageSpecialistName)}
                  clipboardContent={otherLoanData?.brokerMortgageSpecialistName || ''}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(otherLoanData?.brokerMortgageSpecialistName)}
                    cellContent={<span>{otherLoanData?.brokerMortgageSpecialistName}</span>}
                  />
                </CopyContentToClipBoard>
              )}
            />
          </Box>
        </Box>
      )}
    </>
  );
};
